<script lang="ts">
  import { onMount } from "svelte";
  import { authStatus, checkSession } from "./lib/stores/auth";
  import { lists, syncState, initSync } from "./lib/stores/data";
  import LoginPage from "./lib/components/LoginPage.svelte";
  import MenuDrawer from "./lib/components/MenuDrawer.svelte";
  import ListView from "./lib/components/ListView.svelte";
  import AccountPage from "./lib/components/AccountPage.svelte";
  import AdminPage from "./lib/components/AdminPage.svelte";

  let page = $state<"lists" | "admin" | "account">("lists");
  let selectedListId = $state<string | null>(null);
  let drawerOpen = $state(false);
  let syncStarted = false;

  onMount(() => {
    void checkSession();
  });

  $effect(() => {
    if ($authStatus === "authenticated" && !syncStarted) {
      syncStarted = true;
      void initSync();
    }
  });

  $effect(() => {
    if (selectedListId === null && $lists.length > 0) {
      selectedListId = $lists[0]!.id;
    }
    if (selectedListId && !$lists.some((l) => l.id === selectedListId)) {
      selectedListId = $lists[0]?.id ?? null;
    }
  });

  let selectedList = $derived($lists.find((l) => l.id === selectedListId) ?? null);
</script>

{#if $authStatus === "loading"}
  <div class="loading">Loading…</div>
{:else if $authStatus === "anonymous"}
  <LoginPage />
{:else if page === "account"}
  <AccountPage onClose={() => (page = "lists")} />
{:else if page === "admin"}
  <AdminPage onClose={() => (page = "lists")} />
{:else}
  <div class="shell">
    <header class="topbar">
      <button class="menu-btn" onclick={() => (drawerOpen = true)} type="button" aria-label="Open menu">
        ☰
      </button>
      <h1>{selectedList?.name ?? "Groceries"}</h1>
      {#if $syncState === "offline"}
        <span class="badge offline" title="Offline — changes will sync when reconnected">Offline</span>
      {/if}
    </header>

    {#if selectedList}
      <ListView list={selectedList} />
    {:else}
      <p class="empty">No lists yet. Open the menu to create one.</p>
    {/if}
  </div>

  {#if drawerOpen}
    <MenuDrawer
      {selectedListId}
      onSelectList={(id) => (selectedListId = id || null)}
      onNavigate={(target) => {
        page = target;
        drawerOpen = false;
      }}
      onClose={() => (drawerOpen = false)}
    />
  {/if}
{/if}

<style>
  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100dvh;
    color: var(--text-muted);
  }
  .shell {
    display: flex;
    flex-direction: column;
    height: 100dvh;
  }
  .topbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.6rem 0.75rem;
    /* iOS standalone mode (apple-mobile-web-app-status-bar-style:
       black-translucent) draws the app under the status bar/notch — pad it
       back out so the header isn't obscured. No-op elsewhere. */
    padding-top: max(0.6rem, env(safe-area-inset-top));
    padding-left: max(0.75rem, env(safe-area-inset-left));
    padding-right: max(0.75rem, env(safe-area-inset-right));
    border-bottom: 1px solid var(--border);
    background: var(--surface);
  }
  .menu-btn {
    background: none;
    border: none;
    font-size: 1.4rem;
    cursor: pointer;
    color: var(--text);
    padding: 0.25rem 0.5rem;
  }
  h1 {
    flex: 1;
    margin: 0;
    font-size: 1.1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 999px;
  }
  .badge.offline {
    background: var(--danger);
    color: white;
  }
  .empty {
    text-align: center;
    color: var(--text-muted);
    padding: 2rem 1rem;
  }
</style>
